import { type FormEvent, useCallback, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import * as api from "./api";
import { DetailView } from "./components/detail-view";
import { ListView } from "./components/list-view";
import "./styles.css";
import type { AbstractDetail, AbstractSummary } from "./types";

function App() {
	const [searchQuery, setSearchQuery] = useState("");
	const [results, setResults] = useState<AbstractSummary[]>([]);
	const [selectedAbstract, setSelectedAbstract] =
		useState<AbstractDetail | null>(null);
	const [loading, setLoading] = useState(true);

	const loadAll = useCallback(() => {
		setSearchQuery("");
		setSelectedAbstract(null);
		setLoading(true);
		api
			.fetchAbstracts()
			.then(setResults)
			.catch(console.error)
			.finally(() => setLoading(false));
	}, []);

	useEffect(() => {
		loadAll();
	}, [loadAll]);

	const handleSearch = useCallback(
		(e: FormEvent) => {
			e.preventDefault();
			const q = searchQuery.trim();
			if (!q) {
				loadAll();
				return;
			}
			setSelectedAbstract(null);
			setLoading(true);
			api
				.searchAbstracts(q)
				.then(setResults)
				.catch(console.error)
				.finally(() => setLoading(false));
		},
		[searchQuery, loadAll],
	);

	const openAbstract = useCallback((id: string) => {
		setLoading(true);
		api
			.fetchAbstract(id)
			.then((data) => {
				setSelectedAbstract(data);
				window.scrollTo(0, 0);
			})
			.catch(console.error)
			.finally(() => setLoading(false));
	}, []);

	if (selectedAbstract) {
		return (
			<DetailView
				abstract={selectedAbstract}
				onBack={() => setSelectedAbstract(null)}
			/>
		);
	}

	return (
		<ListView
			searchQuery={searchQuery}
			results={results}
			loading={loading}
			onSearchChange={setSearchQuery}
			onSearchSubmit={handleSearch}
			onClearSearch={loadAll}
			onSelectAbstract={openAbstract}
		/>
	);
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Missing #root element");
createRoot(rootEl).render(<App />);
